/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {ActionTree, Commit} from 'vuex';
import {RootState} from '@/store/types';
import {AxiosResponse} from 'axios';
import {auth} from '@/services/http';
import {
    PkceRefreshRequestInterface,
    PkceTokenRequestInterface,
    PkceTokenResponseInterface,
} from '@/services/pkce/types';
import {UserSessionStateInterface} from '@/store/modules/user-session/types';
import moment from 'moment';

/**
 * Actions file for user session operations.
 *
 * Actions are functions that can be exported and called upon potentially in an asynchronous manner.
 * To add an action export a named const function. e.g. export const fnName = ({ commit }, {id, name}) => { ... }
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const actions: ActionTree<UserSessionStateInterface, RootState> = {
    /**
     * Authenticate a user.
     *
     * @param {Commit} commit
     * @param {PkceTokenRequestInterface} params
     *
     * @returns {Promise<PkceTokenResponseInterface>}
     */
    authenticate({commit}: {commit: Commit}, params: PkceTokenRequestInterface): Promise<PkceTokenResponseInterface> {
        const formData: FormData = Object.keys(params).reduce((reducer: FormData, key: string): FormData => {
            reducer.append(key, params[key]);
            return reducer;
        }, new FormData());
        return auth.post('token', formData, {})
            .then((response: AxiosResponse<PkceTokenResponseInterface>) => {
                    commit('authenticate', {
                        token: response.data.access_token,
                        refresh: response.data.refresh_token,
                        expiry: moment().unix() + response.data.expires_in,
                    });
                    return response.data;
                });
    },

    /**
     * Refresh a user token.
     *
     * @param {Commit} commit
     * @param {PkceTokenRequestInterface} params
     *
     * @returns {Promise<PkceTokenResponseInterface>}
     */
    refresh({commit}: {commit: Commit}, params: PkceRefreshRequestInterface): Promise<PkceTokenResponseInterface> {
        const formData: FormData = Object.keys(params).reduce((reducer: FormData, key: string): FormData => {
            reducer.append(key, params[key]);
            return reducer;
        }, new FormData());
        return auth.post('token', formData, {})
            .then((response: AxiosResponse<PkceTokenResponseInterface>) => {
                commit('authenticate', {
                    token: response.data.access_token,
                    refresh: response.data.refresh_token,
                    expiry: moment().unix() + response.data.expires_in,
                });
                return response.data;
            });
    },

    /**
     * Update the session verifier for a new request.
     * @param {Commit} commit
     * @param {string} verifier
     */
    updateVerifier({commit}: {commit: Commit}, verifier: string): void {
        commit('authenticate', {verifier});
    },

    /**
     * Forces a new token onto the stack.
     * @param {Commit} commit
     * @param {string} token
     */
    addToken({commit}: {commit: Commit}, token: string): void {
        commit('authenticate', {
            token,
            refresh: '',
            expiry: moment().unix() + (60 * 60 * 24),
        });
    },

    /**
     * Logout of the user session.
     *
     * As we are using JWT tokens we just need to remove the reference to the token in the localStorage.
     *
     * @param {Promise<AxiosResponse>} commit
     */
    logout({commit}: {commit: Commit}): void {
        commit('logout');
    },
};
